// Quiz → package recommendation engine.
//
// Pure client-side scoring against the EDOS package list. No API calls.
// Each quiz answer contributes a set of weighted "tags". Each package gets
// scored by how many of its searchable fields (test_name, parameters,
// diseases, clinical_aid, specialisation) hit the active tags. Highest score
// wins; ties broken by lower price (more accessible default).
//
// Returns: { primary, alternatives: [pkg, pkg], why: [string, string, string] }
// where `primary` and each `alternatives[i]` is a raw EDOS row from
// /get-edos/packages, NOT a mapped card object.
//
// `answers` is { age, concern, family } — the three quiz steps. Any of
// them may be undefined (e.g. if the user backtracked).
//
// Routing intent (mirrors the reference design):
//   • `concern` dominates — it's what the user explicitly asked about.
//   • `family` reinforces relevant concerns (diabetes/heart/thyroid).
//   • `age` is the fallback when `concern === 'general'` and decides
//     whether we steer toward essential / advanced / premium tier.

// ─── Tag dictionary ──────────────────────────────────────────────────────
// Each tag → list of lowercase keywords that score against package fields.
// Keep keywords short and distinctive; prefer parameter names that actually
// appear in EDOS data.
const TAGS = {
  diabetes:        ['hba1c', 'glucose', 'insulin', 'sugar', 'diabet'],
  thyroid:         ['thyroid', 'tsh', 't3', 't4'],
  cardiac:         ['lipid', 'cholesterol', 'ldl', 'hdl', 'cardiac', 'troponin', 'ck-mb'],
  hairloss:        ['hair', 'ferritin', 'biotin'],
  fitness:         ['fitness', 'cpk', 'creatinine kinase', 'performance', 'athletic'],
  obesity:         ['obesity', 'weight', 'cortisol', 'metabolic'],
  sedentary:       ['sedentary', 'lifestyle'],
  skincare:        ['skincare', 'skin', 'ige', 'allergy'],
  nutrition:       ['nutrient', 'vitamin', 'b12', 'folate', 'iron', 'magnesium', 'calcium'],
  fever:           ['fever', 'malaria', 'widal', 'dengue', 'typhoid', 'esr'],
  senior:          ['senior', 'citizen'],
  female:          ['female', 'women', 'fsh', 'lh', 'prolactin', 'estradiol'],
  male:            ['male', 'testosterone', 'psa', 'prostate'],
  essential:       ['essential'],
  advanced:        ['advanced'],
  fullbody:        ['health checkup', 'full body', 'wellness'],
  coagulation:     ['thrombo', 'coagulation', 'protein c', 'protein s', 'factor'],
  anaemia:         ['thalassemia', 'haemoglobin', 'rbc', 'anaemia', 'anemia'],
};

// ─── Quiz answer → tag weights ───────────────────────────────────────────
// Each quiz answer gets a map of `tag: weight`. Weights are summed across
// all 3 selected answers; the package whose searchable text matches the
// highest-weighted tags wins.
//
// Weights are intentionally coarse (3 / 5 / 8 / 10). The exact numbers
// aren't sacred — they just have to rank tags relative to each other.

// Quiz schema (kept in sync with QUIZ_STEPS in Packages.js):
//   Q1 `reason`   — general | symptoms | condition | fitness
//   Q2 `detail`   — depends on `reason`
//   Q3 `lasttest` — recent | moderate | old | never
const ANSWER_WEIGHTS = {
  // Q1 — reason. Light influence; the actual specific concern lives in Q2.
  reason: {
    general:   { fullbody: 6, essential: 6 },
    symptoms:  { nutrition: 5, advanced: 5 },
    condition: { advanced: 6 },
    fitness:   { fitness: 8, nutrition: 4 },
  },

  // Q2 — detail. Heaviest weights — this is the user's explicit ask.
  // The keys are the union of all `detail` option values from QUIZ_STEPS.
  detail: {
    // symptoms.*
    fatigue:     { nutrition: 10, thyroid: 6 },
    weight:      { obesity: 10, diabetes: 6 },
    hair:        { hairloss: 10, thyroid: 6 },
    none:        { fullbody: 6 },
    // condition.*
    diabetes:    { diabetes: 12, obesity: 4 },
    thyroid:     { thyroid: 12 },
    cardiac:     { cardiac: 12, advanced: 4 },
    pcod:        { female: 10, thyroid: 4 },
    // fitness.*
    performance: { fitness: 12, nutrition: 4 },
    weightloss:  { obesity: 10, diabetes: 4 },
    endurance:   { nutrition: 8, anaemia: 6 },
    recovery:    { nutrition: 8 },
    // general.*
    first:       { essential: 8, fullbody: 6 },
    annual:      { fullbody: 8, advanced: 4 },
    referred:    { advanced: 8 },
    family:      { advanced: 6, cardiac: 4, diabetes: 4 },
  },

  // Q3 — last test. Mild influence; mostly drives the "why" copy.
  lasttest: {
    recent:   { essential: 4 },
    moderate: { fullbody: 4 },
    old:      { advanced: 4, fullbody: 6 },
    never:    { advanced: 6, fullbody: 8 },
  },
};

// Per-answer "why" copy shown as bullet points on the result card.
// Strictly static — readable, friendly, doesn't pretend to be personalised
// beyond reflecting the user's actual selections.
const WHY_COPY = {
  reason: {
    general:   'You wanted a routine wellness screen — this covers what your doctor checks first.',
    symptoms:  'Symptoms can point in several directions — we have widened the panel to find the cause.',
    condition: 'For an existing condition, we go deeper than a routine screen — focused markers plus a full body baseline.',
    fitness:   'Performance-tuned — markers that matter for training, recovery, and metabolism.',
  },
  detail: {
    fatigue:     'Fatigue workup — thyroid, vitamin B12, vitamin D, ferritin, magnesium and cortisol.',
    weight:      'Unexplained weight changes — thyroid, HbA1c, lipid + liver enzymes, cortisol.',
    hair:        'Hair fall workup — ferritin, thyroid, vitamin D, biotin, zinc.',
    none:        'Routine surveillance — standard wellness panel and metabolic markers.',
    diabetes:    'Diabetes panel — HbA1c, fasting + post-prandial glucose, insulin, kidney function, urine ACR.',
    thyroid:     'Full thyroid panel — TSH, Free T3, Free T4, anti-TPO antibodies.',
    cardiac:     'Cardiac risk panel — full lipid breakdown, hsCRP, homocysteine, kidney function.',
    pcod:        'Hormonal profile — LH, FSH, prolactin, testosterone, AMH, thyroid, insulin, vitamin D.',
    performance: 'Performance-tuned — testosterone, cortisol, ferritin, CK, electrolytes.',
    weightloss:  'Weight-loss workup — fasting insulin, HOMA-IR, thyroid, cortisol, lipid breakdown.',
    endurance:   'Stamina workup — iron, ferritin, B12, folate, electrolytes, vitamin D.',
    recovery:    'Recovery panel — CBC with ESR, vitamin profile, electrolytes, kidney + liver function.',
    first:       'A clean baseline to start from — covers the standard wellness markers.',
    annual:      'Annual surveillance — standard wellness with a few advanced markers.',
    referred:    'Doctor-referred — broader than routine; includes deeper diagnostic markers.',
    family:      'Family history — extra weight on cardiac, diabetes, and thyroid markers.',
  },
  lasttest: {
    recent:   'Light surveillance since you already have recent results to compare against.',
    moderate: 'Time to refresh — a full panel with markers comparable to your last check.',
    old:      'Comprehensive panel — fills the gap since your last check.',
    never:    'First-ever baseline — captures every standard marker so future checks have a reference point.',
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────
const fieldsOf = (pkg) => [
  pkg?.test_name,
  pkg?.parameters,
  pkg?.diseases,
  pkg?.clinical_aid,
  pkg?.specialisation,
].filter(Boolean).join(' ').toLowerCase();

const containsAny = (haystack, needles) =>
  needles.some((n) => haystack.includes(n));

// The three quiz step keys, in the order they're rendered. Used both by
// `collectTags` (sum weights across answered steps) and by the "why" copy
// builder (preserve the same order in the result card bullets).
const STEP_KEYS = ['reason', 'detail', 'lasttest'];

// Build the active tag→weight map from the user's quiz answers.
const collectTags = (answers) => {
  const out = {};
  STEP_KEYS.forEach((step) => {
    const val = answers?.[step];
    const map = ANSWER_WEIGHTS[step]?.[val];
    if (!map) return;
    Object.entries(map).forEach(([tag, w]) => {
      out[tag] = (out[tag] || 0) + w;
    });
  });
  return out;
};

// Score a single package against the active tags.
const scorePackage = (pkg, tagWeights) => {
  const text = fieldsOf(pkg);
  let score = 0;
  Object.entries(tagWeights).forEach(([tag, weight]) => {
    const keywords = TAGS[tag];
    if (!keywords) return;
    if (containsAny(text, keywords)) score += weight;
  });
  return score;
};

// Pick a sensible "featured" package for the idle state and the quiz result
// state. Both surfaces should land on the same flagship — "TRUST Advanced
// Health Checkup" — so the brand always foregrounds the same hero panel.
// Tries the broader "advanced ... health ... checkup" pattern first, then
// a generic fallback. Falls back to the first package in the list.
export const pickFeatured = (packages = []) => {
  if (!packages.length) return null;
  const byTrust = packages.find((p) =>
    /trust.*advanced.*health.*checkup/i.test(p.test_name || '')
  );
  if (byTrust) return byTrust;
  const byAdvanced = packages.find((p) =>
    /advanced.*health.*checkup/i.test(p.test_name || '')
  );
  return byAdvanced || packages[0];
};

// ─── Public API ──────────────────────────────────────────────────────────
// The quiz result picks the BEST-scoring package based on the user's
// answers; alternatives are the next two top-scoring distinct packages.
// If no answers score (idle / pre-quiz state) we surface the featured
// pick so the card still renders.
export const recommendPackage = (answers, packages = []) => {
  if (!packages.length) return { primary: null, alternatives: [], why: [] };

  const tagWeights = collectTags(answers);

  // No meaningful answers — idle / pre-quiz state. Show the featured pick
  // with no "why" copy and no alternatives.
  if (Object.keys(tagWeights).length === 0) {
    return { primary: pickFeatured(packages), alternatives: [], why: [] };
  }

  // Score the whole catalogue, rank by score (then by lower price).
  const scored = packages
    .map((p) => ({
      pkg: p,
      score: scorePackage(p, tagWeights),
      price: Number(p.l2l_price) || Number(p.mrp) || Infinity,
    }))
    .filter((x) => x.score > 0)
    .sort((a, b) => (b.score - a.score) || (a.price - b.price));

  // Nothing scored — fall back to the featured pick so the hero stays full.
  if (scored.length === 0) {
    return {
      primary: pickFeatured(packages),
      alternatives: [],
      why: ['Based on what you told us, this is the closest match in our catalogue.'],
    };
  }

  const primary = scored[0].pkg;
  const alternatives = scored.slice(1, 3).map((x) => x.pkg);

  // Build the "why" bullets from the user's actual answers (skip empties).
  const why = STEP_KEYS
    .map((step) => WHY_COPY[step]?.[answers?.[step]] || '')
    .filter(Boolean);

  return { primary, alternatives, why };
};
